import {useEffect, useRef} from "react";
import {useMap} from "react-leaflet";
import L from "leaflet";

import {OfferType} from "./mocks/singleOfferMock";
import {MARKER_CLUSTER_DISABLE_ZOOM} from "./marker_cluster_constants";
import {OFFER_LIST_MARKER_BORDER_COLOR, offerListMarkerClusterColor} from "./offer_list_marker_icon";

import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster";

const OFFER_LIST_TYPE_OPTION = "offerListType";

type OfferListMarkerOptions = L.MarkerOptions & {
    [OFFER_LIST_TYPE_OPTION]?: number;
};

const clusterIconSize = (count: number): number => {
    if (count < 10) {
        return 36;
    }
    if (count < 100) {
        return 42;
    }
    return 48;
};

const resolveClusterColor = (cluster: L.MarkerCluster): string => {
    let houseCount = 0;
    let flatCount = 0;

    for (const child of cluster.getAllChildMarkers()) {
        const type = (child.options as OfferListMarkerOptions)[OFFER_LIST_TYPE_OPTION];
        if (type === OfferType.HOUSE) {
            houseCount++;
        } else {
            flatCount++;
        }
    }

    return offerListMarkerClusterColor(houseCount > flatCount ? OfferType.HOUSE : OfferType.FLAT);
};

const createClusterIcon = (cluster: L.MarkerCluster): L.DivIcon => {
    const count = cluster.getChildCount();
    const size = clusterIconSize(count);
    const color = resolveClusterColor(cluster);
    return L.divIcon({
        html: `<div style="background-color:${color};width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:${
            count < 100 ? 14 : 12
        }px;line-height:1;border:2px solid ${OFFER_LIST_MARKER_BORDER_COLOR};box-shadow:0 2px 6px rgba(0,0,0,0.28);"><span>${count}</span></div>`,
        className: "charts-marker-cluster",
        iconSize: L.point(size, size)
    });
};

export interface ClusterMarkerDef {
    id: string | number;
    coords: [number, number];
    popup?: string;
    onClick?: () => void;
    /** CBI `type` — cluster color + tooltip copy (`OfferType.FLAT` / `HOUSE`). */
    offerListType: number;
    icon?: {
        url: string;
        sizes: [number, number];
    };
}

const toLeafletIcon = (icon: ClusterMarkerDef["icon"]): L.Icon | undefined => {
    if (!icon) {
        return undefined;
    }
    return L.icon({
        iconUrl: icon.url,
        iconSize: icon.sizes,
        iconAnchor: [icon.sizes[0] / 2, icon.sizes[1]],
        tooltipAnchor: [0, -icon.sizes[1]],
        popupAnchor: [0, -Math.max(icon.sizes[1] - 4, 8)]
    });
};

const bindMarkerTooltip = (leafletMarker: L.Marker, popup: string | undefined): void => {
    leafletMarker.unbindTooltip();
    leafletMarker.unbindPopup();
    leafletMarker.closePopup();

    if (!popup) {
        return;
    }

    leafletMarker.bindTooltip(popup, {direction: "top", opacity: 0.95});
};

const setOfferListType = (leafletMarker: L.Marker, offerListType: number): void => {
    (leafletMarker.options as OfferListMarkerOptions)[OFFER_LIST_TYPE_OPTION] = offerListType;
};

const applyMarkerDef = (leafletMarker: L.Marker, marker: ClusterMarkerDef): void => {
    setOfferListType(leafletMarker, marker.offerListType);
    const icon = toLeafletIcon(marker.icon);
    if (icon) {
        leafletMarker.setIcon(icon);
    }
    leafletMarker.off("click");
    if (marker.onClick) {
        leafletMarker.on("click", marker.onClick);
    }
    bindMarkerTooltip(leafletMarker, marker.popup);
};

const buildLeafletMarker = (marker: ClusterMarkerDef): L.Marker => {
    const leafletMarker = L.marker(marker.coords, {
        icon: toLeafletIcon(marker.icon),
        [OFFER_LIST_TYPE_OPTION]: marker.offerListType
    } as OfferListMarkerOptions);
    applyMarkerDef(leafletMarker, marker);
    return leafletMarker;
};

interface MarkerClusterLayerProps {
    markers: ClusterMarkerDef[];
    disableClusteringAtZoom?: number;
}

/** Imperative `L.markerClusterGroup` — keeps listing maps readable at city zoom, individual pins at district zoom. */
export const MarkerClusterLayer = ({
    markers,
    disableClusteringAtZoom = MARKER_CLUSTER_DISABLE_ZOOM
}: MarkerClusterLayerProps) => {
    const map = useMap();
    const groupRef = useRef<L.MarkerClusterGroup | null>(null);
    const markerLayersRef = useRef<Map<string | number, L.Marker>>(new Map());

    useEffect(() => {
        const group = L.markerClusterGroup({
            disableClusteringAtZoom,
            maxClusterRadius: 64,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            iconCreateFunction: createClusterIcon
        });
        groupRef.current = group;
        map.addLayer(group);
        return () => {
            map.removeLayer(group);
            groupRef.current = null;
            markerLayersRef.current.clear();
        };
    }, [map, disableClusteringAtZoom]);

    useEffect(() => {
        const group = groupRef.current;
        if (!group) {
            return;
        }

        const layersById = markerLayersRef.current;
        const nextIds = new Set(markers.map((m) => m.id));
        let structureChanged = false;

        for (const [id, layer] of layersById) {
            if (!nextIds.has(id)) {
                group.removeLayer(layer);
                layersById.delete(id);
                structureChanged = true;
            }
        }

        for (const marker of markers) {
            const existing = layersById.get(marker.id);
            if (existing) {
                applyMarkerDef(existing, marker);
            } else {
                const layer = buildLeafletMarker(marker);
                group.addLayer(layer);
                layersById.set(marker.id, layer);
                structureChanged = true;
            }
        }

        if (structureChanged) {
            group.refreshClusters();
        }
    }, [markers]);

    return null;
};
